import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { Id } from '@contracts/common';
import type { BatchOptionFor, BatchRowDTO, BatchRowKind, IngestJobDTO } from '@contracts/ingest';
import { useNarrowView } from '../../../lib/media';
import { cn } from '../../../lib/utils';
import { PRIORITY_LABEL } from '../../../lib/labels';
import { useActiveUsers } from '../../tasks/RecipientField';
import { useAssistant } from '../AssistantProvider';
import { formatValue, FILLED_NAME, pickOf, REF_KEY, shortDay } from './fields';
import { BATCH_GROUPS, OP_LABEL, rowTotal } from './labels';
import { RefPicker } from './RefPicker';
import { RowEditor } from './RowEditor';
import { SourceLink } from './SourceLink';
import { useRowWriter } from './useRowWriter';

/** §8.1: the card shows this many rows; the rest are on the batch's own page. */
export const ROWS_SHOWN = 20;

const MUTED = 'text-[hsl(var(--h-muted-foreground))]';
const ERROR = 'text-xs font-medium text-[hsl(var(--h-error-fg))]';
export const PILL = 'inline-block whitespace-nowrap rounded-[9px] px-[7px] align-[1px] text-[11px] font-bold leading-[18px]';
export const PILL_TONE = {
  create: 'bg-[hsl(var(--h-info-soft))] text-[hsl(var(--h-info-fg))]',
  update: 'bg-[hsl(var(--h-warning-soft))] text-[hsl(var(--h-warning-fg))]',
  duplicate: 'bg-[hsl(var(--h-late-soft))] text-[hsl(var(--h-late))]',
  needs_fix: 'bg-[hsl(var(--h-warning-soft))] text-[hsl(var(--h-warning-fg))]',
  rejected: 'bg-[hsl(var(--h-muted))] text-[hsl(var(--h-muted-foreground))]',
} as const;
const CHIP = 'rounded-[5px] bg-[hsl(var(--h-muted))] px-1.5 py-px text-[hsl(var(--h-card-foreground))]';
/** D140: a reference chip never breaks inside itself; a long one is cut, its whole label in the title. */
function Chip({ label }: { label: string }) {
  return (
    <span title={label} className={cn(CHIP, 'inline-block max-w-full truncate whitespace-nowrap align-bottom')}>{label}</span>
  );
}

function pillOf(row: BatchRowDTO): { label: string; tone: keyof typeof PILL_TONE } {
  if (row.status === 'rejected') return { label: OP_LABEL.rejected, tone: 'rejected' };
  if (row.status === 'needs_fix') return { label: OP_LABEL.needs_fix, tone: 'needs_fix' };
  if (row.status === 'duplicate') return { label: OP_LABEL.duplicate, tone: 'duplicate' };
  if (row.kind === 'note') return { label: OP_LABEL.note, tone: 'create' };
  return row.op === 'update' ? { label: OP_LABEL.update, tone: 'update' } : { label: OP_LABEL.create, tone: 'create' };
}

export const seqParent = (row: BatchRowDTO): number | null =>
  row.parentRef !== null && 'seq' in row.parentRef ? row.parentRef.seq : null;

/** A group's rows with each child under its parent (depth for the indent); a child whose parent is elsewhere leads. */
function treeOrder(rows: readonly BatchRowDTO[]): { row: BatchRowDTO; depth: number }[] {
  const seqs = new Set(rows.map((r) => r.seq));
  const children = new Map<number, BatchRowDTO[]>();
  const top: BatchRowDTO[] = [];
  for (const r of [...rows].sort((a, b) => a.seq - b.seq)) {
    const p = seqParent(r);
    if (p !== null && seqs.has(p)) children.set(p, [...(children.get(p) ?? []), r]);
    else top.push(r);
  }
  const out: { row: BatchRowDTO; depth: number }[] = [];
  const walk = (r: BatchRowDTO, depth: number) => {
    out.push({ row: r, depth });
    for (const c of children.get(r.seq) ?? []) walk(c, depth + 1);
  };
  for (const r of top) walk(r, 0);
  return out;
}

/**
 * The heading's context (mockup §1): every row under one card, else every row of one project — by its name alone
 * (D136; a finance row's project carries it). `complete`: every row of the group is loaded, so "every" is known; else
 * the heading claims nothing. `project`: the heading names the project, so the rows leave their project chip out.
 */
function headingOf(label: string, rows: readonly BatchRowDTO[], complete: boolean): { text: string; project: boolean } {
  const plain = { text: label, project: false };
  if (!complete) return plain;
  const top = rows.filter((r) => seqParent(r) === null);
  // A row naming a card, or a new one placed by the batch's own card (the drop's, or "Đặt dưới", D152).
  const parents = new Set(top.map((r) => r.view.parent?.label));
  const [parent] = parents;
  if (top.length > 0 && parents.size === 1 && parent !== undefined) return { text: `${label} · con của "${parent}"`, project: false };
  const projects = new Set(rows.map((r) => r.view.refs.projectId?.label));
  const [project] = projects;
  if (projects.size === 1 && project !== undefined) {
    const ref = rows[0]!.view.refs.projectId!;
    const name = (ref.name ?? ref.label).trim();
    // A project already named "Dự án …" is not called "dự án Dự án …" (D142).
    const named = name.toLocaleLowerCase('vi').startsWith('dự án') ? name : `dự án ${name}`;
    return { text: `${label} · ${named}`, project: true };
  }
  return plain;
}

const titleOf = (row: BatchRowDTO): string => row.view.title;
const directionOf = (row: BatchRowDTO): string | null =>
  row.kind !== 'finance_entry' ? null : row.fields.direction === 'in' ? 'Thu' : row.fields.direction === 'out' ? 'Chi' : null;

/** The row's second line: who and when for a task, how much and when for money, the records it hangs on otherwise. */
/**
 * `anchored`: the batch hangs off a card, the parent of a row that names none. A row with no parent at all was filled
 * by the board's own rule (the asker, day + 30), so it says Hana filled it, not that a parent card did.
 */
function Meta({ row, anchored, projectInHeading }: { row: BatchRowDTO; anchored: boolean; projectInHeading: boolean }) {
  const { prefs } = useAssistant();
  const users = useActiveUsers().data;
  const parts: ReactNode[] = [];
  const f = row.fields;
  if (row.kind === 'task') {
    const assignee = typeof f.assigneeId === 'string' ? users?.find((u) => u.id === f.assigneeId)?.name : undefined;
    if (assignee !== undefined) parts.push(assignee);
    const day = shortDay(f.deadline);
    if (day !== null) parts.push(`hạn ${day}`);
    if (f.priority === 'high' || f.priority === 'urgent') parts.push(PRIORITY_LABEL[f.priority]);
  } else if (row.kind === 'finance_entry') {
    const amount = f.actualAmount ?? f.plannedAmount;
    if (typeof amount === 'string' && amount !== '') {
      parts.push(<b key="amount" className="font-semibold text-[hsl(var(--h-card-foreground))]">{formatValue('plannedAmount', amount)}</b>);
    }
    const day = shortDay(f.actualDate ?? f.plannedDate);
    if (day !== null) parts.push(f.actualDate != null ? day : `dự kiến ${day}`);
  }
  const refs = row.view.refs;
  if (refs.projectId !== undefined && row.kind === 'finance_entry' && !projectInHeading) {
    parts.push(<span key="project">dự án <Chip label={refs.projectId.label} /></span>);
  }
  if (refs.categoryId !== undefined) parts.push(`danh mục ${refs.categoryId.label}`);
  if (refs.partnerId !== undefined) parts.push(<span key="partner">đối tác <Chip label={refs.partnerId.label} /></span>);
  if (refs.objectiveId !== undefined) parts.push(`mục tiêu ${refs.objectiveId.label}`);
  const from = row.parentRef !== null || anchored ? ' theo thẻ cha' : '';
  for (const field of row.filled) {
    const name = FILLED_NAME[field];
    if (name !== undefined) parts.push(<i key={`filled-${field}`}>{`${name} do ${prefs.personaName} điền${from}`}</i>);
  }
  if (parts.length === 0) return null;
  return (
    <p className={cn('text-xs', MUTED)}>
      {parts.map((p, i) => <span key={i}>{i > 0 && ' · '}{p}</span>)}
    </p>
  );
}

/** Where the page's virtual list puts a row (§8.2): its index, its offset, and the measuring ref. */
export type RowPlace = { index: number; start: number; measure: (el: HTMLElement | null) => void };
/**
 * The page's table columns (mockup §2): a finance batch reads like a statement (Ngày, Diễn giải, Dự án · danh mục,
 * Số tiền); any other batch has one content column with the card's meta line. Shared by the header and the rows.
 */
export const TABLE_COLUMNS = {
  finance: 'grid-cols-[40px_52px_6.5rem_minmax(0,1fr)_minmax(0,11rem)_9.5rem_7.5rem_36px]',
  other: 'grid-cols-[40px_52px_minmax(0,1fr)_7.5rem_36px]',
} as const;
export type RowLayout = 'card' | keyof typeof TABLE_COLUMNS;

/** The row's place in its document as the table's "Dòng" column writes it: the sheet row, the page, else its order. */
export function positionOf(row: BatchRowDTO): string {
  const s = row.source;
  if (s.row != null) return String(s.row);
  if (s.page != null) return `tr.${s.page}`;
  if (s.cell != null) return s.cell;
  return String(row.seq + 1);
}

/** "−45.000.000 ₫" money out, "+120.000.000 ₫" money in (mockup §2 Số tiền). */
function signedAmount(row: BatchRowDTO): { text: string; inflow: boolean } | null {
  const amount = row.fields.actualAmount ?? row.fields.plannedAmount;
  if (typeof amount !== 'string' || amount === '') return null;
  const inflow = row.fields.direction === 'in';
  const sign = row.fields.direction === 'out' ? '−' : inflow ? '+' : '';
  return { text: `${sign}${formatValue('plannedAmount', amount)}`, inflow };
}

const CELL = 'min-w-0 px-2 py-2';

/**
 * One row of a ready batch: its box, its pill and title (opening the editor), what changes, and what it needs. The
 * card draws it as a list item; the page (§8.2) places it in its virtual list, as a table row at lg (`finance` or
 * `other` columns) and as the same card below it, and shows the rows Kiểm left out there too (never checkable).
 */
export function BatchRow({ job, row, depth, marked, layout = 'card', place, projectInHeading = false }: {
  job: IngestJobDTO; row: BatchRowDTO; depth: number;
  /** D136: the group heading names the row's project, so the row leaves its chip out. */
  projectInHeading?: boolean;
  /** The reasons a refused Duyệt gave for this row (BATCH_CHANGED): the row is tinted until the next Duyệt. */
  marked?: readonly string[];
  layout?: RowLayout;
  place?: RowPlace;
}) {
  const writer = useRowWriter(job.id, row);
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState<boolean | null>(null);
  const checked = checking ?? row.included;
  const pill = pillOf(row);
  const title = titleOf(row);
  const table = layout !== 'card';
  const finance = layout === 'finance';
  const direction = finance ? null : directionOf(row);
  const fixing = row.status === 'needs_fix';
  const rejected = row.status === 'rejected';
  const opError = writer.error !== null && (writer.error.field === 'op' || writer.error.field === 'targetId');
  const rowError = writer.error !== null && !(open && opError) ? writer.error.message : null;

  const pick = (of: BatchOptionFor, id: Id) => {
    if (of === 'target') writer.send(row.op === 'update' ? { targetId: id } : { op: 'update', targetId: id });
    else writer.send({ refs: { [REF_KEY[of]]: id } });
  };

  const box = (
    <label className={cn('-my-1 flex min-h-11 min-w-11 shrink-0 cursor-pointer items-start justify-center pt-[7px] lg:min-h-0 lg:min-w-0 lg:pt-[3px]',
      table ? 'lg:my-0 lg:pt-[11px]' : 'lg:pl-2')}>
      <input type="checkbox" aria-label={`Chọn dòng: ${title}`} checked={checked} disabled={fixing || rejected}
        onChange={() => {
          const next = !checked;
          setChecking(next);
          writer.send({ included: next }, () => setChecking(null));
        }}
        className="size-4 cursor-pointer accent-[hsl(var(--h-primary))] disabled:cursor-default" />
    </label>
  );
  const pillNode = <span className={cn(PILL, PILL_TONE[pill.tone])}>{pill.label}</span>;
  // What a needs-fix row asks, as a picker where the row can pick it. On the finance table the project and the
  // category are picked in their own column, "Dự án · danh mục" (mockup §2), not under Diễn giải.
  const fixOf = (reason: string) => {
    const of = pickOf(row.kind, reason);
    return of === null ? <span key={reason} className="font-medium text-[hsl(var(--h-error-fg))]">{reason}</span> : (
      <RefPicker key={reason} jobId={job.id} from={{ rowId: row.id, of }} label={reason} disabled={writer.writing}
        onPick={(id) => pick(of, id)}>{reason}</RefPicker>
    );
  };
  const inRefs = (reason: string) => {
    const of = pickOf(row.kind, reason);
    return finance && (of === 'project' || of === 'category');
  };
  const refReasons = fixing ? row.reasons.filter(inRefs) : [];
  const detailReasons = fixing ? row.reasons.filter((reason) => !inRefs(reason)) : [];
  const details = (
    <>
      {!finance && <Meta row={row} anchored={job.anchorItemId !== null} projectInHeading={projectInHeading} />}
      {(row.view.changes ?? []).map((c) => (
        <p key={c.field} data-change={c.field} className="text-xs text-[hsl(var(--h-card-foreground))]">
          {`${c.label}: `}<s className={MUTED}>{formatValue(c.field, c.before)}</s>{' → '}
          <b className="font-semibold">{formatValue(c.field, c.after)}</b>
        </p>
      ))}
      {row.status === 'duplicate' && (
        <p className={cn('text-xs', MUTED)}>
          {row.view.duplicateOf !== undefined ? `Nghi trùng ${row.view.duplicateOf.label}` : row.reasons.join(' · ')}
        </p>
      )}
      {fixing && detailReasons.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 text-xs">{detailReasons.map(fixOf)}</div>
      )}
      {(row.status === 'ok' || rejected) && row.reasons.length > 0 && (
        <p className={cn('text-xs', MUTED)}>{row.reasons.join(' · ')}</p>
      )}
      {(marked ?? []).filter((r) => !row.reasons.includes(r)).map((r) => (
        <p key={r} className="text-xs font-medium text-[hsl(var(--h-warning-fg))]">{r}</p>
      ))}
      {rowError !== null && <p role="alert" className={ERROR}>{rowError}</p>}
      {open && (
        <RowEditor jobId={job.id} row={row} value={writer.value} edit={writer.edit} flush={writer.flush}
          send={writer.send} error={writer.error} writing={writer.writing} />
      )}
    </>
  );
  const toggle = () => { if (open) writer.flush(); setOpen(!open); };
  const titleNode = rejected ? <span className="text-[hsl(var(--h-card-foreground))]">{title}</span> : (
    // N2: a span, not a <button> — a button is an atomic inline box, so a long title would drop whole below its pill
    // or the "Chi · " prefix instead of wrapping as running text (mockup §1).
    <span role="button" tabIndex={0} aria-expanded={open} onClick={toggle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
      className="cursor-pointer rounded-sm text-[hsl(var(--h-card-foreground))] underline decoration-[hsl(var(--h-muted-foreground))] decoration-dashed underline-offset-4 [overflow-wrap:anywhere] hover:text-[hsl(var(--h-primary))]">
      {title}
    </span>
  );

  const Root = place === undefined ? 'li' : 'div';
  const indent = (depth > 0 ? 18 : 0) + 4;
  const placed = place === undefined ? {} : {
    'data-index': place.index, ref: place.measure, role: table ? 'row' : 'listitem',
    'aria-rowindex': table ? place.index + 2 : undefined,
  };
  const style = place === undefined ? { paddingLeft: `${indent}px` }
    : { position: 'absolute' as const, top: 0, left: 0, width: '100%', transform: `translateY(${place.start}px)`,
      ...(table ? {} : { paddingLeft: `${indent}px` }) };
  // The page dims what will not be written: a duplicate left unchecked, a row left out (mockup §2).
  const dim = place !== undefined && (rejected || (row.status === 'duplicate' && !checked));
  const tint = marked !== undefined && 'bg-[hsl(var(--h-warning-soft)/0.6)]';
  const attrs = { 'data-batch-row': row.id, 'data-status': row.status, 'data-changed': marked === undefined ? undefined : '' };

  if (table) {
    const day = shortDay(row.fields.actualDate ?? row.fields.plannedDate);
    const amount = signedAmount(row);
    const project = row.view.refs.projectId?.label;
    const category = row.view.refs.categoryId?.label;
    const refs = [
      ...(project === undefined ? [] : [{ key: 'project', label: project, project: true }]),
      ...(category === undefined ? [] : [{ key: 'category', label: category, project: false }]),
    ];
    return (
      <Root {...placed} {...attrs} style={style}
        className={cn('grid items-start border-b border-[hsl(var(--h-border))] text-sm', TABLE_COLUMNS[layout], tint)}>
        <div role="cell" className="flex justify-center">{box}</div>
        <div role="cell" data-cell="position" className={cn(CELL, 'tabular-nums text-xs leading-6', MUTED)}>{positionOf(row)}</div>
        {finance && (
          <div role="cell" className={cn(CELL, 'tabular-nums text-xs leading-6', dim ? MUTED : 'text-[hsl(var(--h-card-foreground))]')}>
            {day === null ? '—' : row.fields.actualDate != null ? day : `dự kiến ${day}`}
          </div>
        )}
        <div role="cell" className={cn(CELL, 'leading-6', dim && 'opacity-70')} style={depth > 0 ? { paddingLeft: '26px' } : undefined}>
          <p className="min-w-0 break-words">
            {direction !== null && <span>{`${direction} · `}</span>}
            {titleNode}
          </p>
          {details}
        </div>
        {finance && (
          <div role="cell" data-cell="refs" className={cn(CELL, 'text-xs leading-6', MUTED)}>
            {/* D191: the project chip fills the column, so each unit takes its own line and no "·" joins them. */}
            {refs.length === 0 && refReasons.length === 0 ? '—' : [
              ...refs.map((ref) => (ref.project ? <div key={ref.key}><Chip label={ref.label} /></div>
                : <div key={ref.key} className="break-words">{ref.label}</div>)),
              ...refReasons.map((reason) => <div key={reason}>{fixOf(reason)}</div>),
            ]}
          </div>
        )}
        {finance && (
          <div role="cell" className={cn(CELL, 'text-right font-semibold tabular-nums leading-6',
            amount?.inflow === true ? 'text-[hsl(var(--h-success-fg))]' : 'text-[hsl(var(--h-card-foreground))]',
            dim && 'opacity-70')}>
            {amount?.text ?? '—'}
          </div>
        )}
        <div role="cell" className={cn(CELL, 'leading-6')}>{pillNode}</div>
        <div role="cell" className="flex justify-center py-1"><SourceLink job={job} source={row.source} compact /></div>
      </Root>
    );
  }

  return (
    <Root {...placed} {...attrs} style={style}
      className={cn('flex items-start gap-1 border-t border-dashed border-[hsl(var(--h-border))] py-1.5 pr-2 lg:gap-2 lg:pr-3',
        place === undefined ? 'first:border-t-0' : place.index === 0 && 'border-t-0', tint)}>
      {box}
      <div className={cn('min-w-0 flex-1 py-px text-sm leading-6', dim && 'opacity-70')}>
        <p className="min-w-0 break-words">
          {pillNode}{' '}
          {direction !== null && <span>{`${direction} · `}</span>}
          {titleNode}
        </p>
        {details}
      </div>
      <SourceLink job={job} source={row.source} />
    </Root>
  );
}

/** The rows Kiểm left out: last, folded to one line, each with why (§8.1). Never checkable. */
function Rejected({ rows }: { rows: readonly BatchRowDTO[] }) {
  const [open, setOpen] = useState(false);
  if (rows.length === 0) return null;
  const head = `${rows.length} dòng không đưa vào lô`;
  const only = rows.length === 1 ? rows[0] : undefined;
  return (
    <div data-batch-rejected="" className="border-t border-[hsl(var(--h-border))] px-3 py-1.5 text-xs">
      <button type="button" aria-expanded={open} onClick={() => setOpen(!open)}
        className={cn('min-h-11 w-full text-left transition-colors hover:text-[hsl(var(--h-card-foreground))] lg:min-h-0 lg:py-0.5', MUTED)}>
        {open ? `▾ ${head}` : only !== undefined
          ? `▸ ${head}: "${titleOf(only)}" · ${only.reasons.join(', ')}` : `▸ ${head}`}
      </button>
      {open && (
        <ul className="flex flex-col gap-1 pb-1 pl-3">
          {rows.map((r) => (
            <li key={r.id} data-batch-row={r.id} data-status="rejected">
              <span className="text-[hsl(var(--h-card-foreground))]">{titleOf(r)}</span>
              {r.reasons.length > 0 && <span className={cn('block', MUTED)}>{r.reasons.join(' · ')}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const groupCount = (byKind: IngestJobDTO['counts']['byKind'], kinds: readonly BatchRowKind[]) =>
  kinds.reduce((n, k) => n + (byKind[k] ?? 0), 0);

/** The card's write of "Đặt dưới" (null: Việc lẻ), and whether a write of the batch is on its way. */
export type Place = { busy: boolean; to: (parentItemId: Id | null) => void };

/**
 * "Đặt dưới: [Chọn thẻ ▾] / Việc lẻ" (§10.1, D152) under the Việc heading of a batch dropped on no card: the card its
 * top rows with no parent of their own go under, or none (Việc lẻ, the default). The list is the cards the first such
 * task row may name (visible only); the choice is the batch's, sent by the card (`PUT …/parent`).
 */
function PlaceUnder({ job, rows, place }: { job: IngestJobDTO; rows: readonly BatchRowDTO[]; place: Place }) {
  const loose = rows.filter((r) => r.parentRef === null && r.op === 'create');
  const asker = loose.find((r) => r.kind === 'task') ?? loose[0];
  if (asker === undefined) return null;
  const chosen = job.defaults.parentItemId !== undefined;
  const label = chosen ? loose.find((r) => r.view.parent !== undefined)?.view.parent?.label : undefined;
  return (
    <div role="group" aria-label="Đặt dưới" className="flex flex-wrap items-center gap-x-1.5 px-3 pb-1 text-xs">
      <span className={MUTED}>Đặt dưới:</span>
      <RefPicker jobId={job.id} from={{ rowId: asker.id, of: 'target' }} label="Chọn thẻ" disabled={place.busy}
        onPick={(id) => place.to(id)} className={cn('text-xs', chosen ? 'rounded-md bg-[hsl(var(--h-primary)/0.1)] px-1.5' : 'font-normal')}>
        {label ?? 'Chọn thẻ'}
      </RefPicker>
      <span aria-hidden="true" className={MUTED}>/</span>
      <button type="button" aria-pressed={!chosen} disabled={place.busy} onClick={() => { if (chosen) place.to(null); }}
        className="inline-flex min-h-11 items-center rounded-md px-1.5 text-[hsl(var(--h-muted-foreground))] transition-colors hover:text-[hsl(var(--h-primary))] disabled:cursor-default disabled:opacity-50 aria-pressed:bg-[hsl(var(--h-primary)/0.1)] aria-pressed:font-semibold aria-pressed:text-[hsl(var(--h-primary))] lg:min-h-0 lg:py-0.5">
        Việc lẻ
      </button>
    </div>
  );
}

/**
 * The ready card's rows (spec §8.1, mockup §1): by module in the §8.1 order, each heading with its count and context,
 * the first `ROWS_SHOWN` rows, then "và M dòng nữa · Mở rộng"; the rows left out come last, folded.
 */
export function BatchRows({ job, rows, changed, place }: {
  job: IngestJobDTO; rows: readonly BatchRowDTO[]; changed?: ReadonlyMap<Id, readonly string[]>;
  /** "Đặt dưới" (D152): given by the card; a batch dropped on a card has its parent already, so none shows. */
  place?: Place;
}) {
  const { setOpen } = useAssistant();
  // On a phone the panel covers the page it opens (§8.1): it closes first.
  const narrow = useNarrowView();
  let budget = ROWS_SHOWN;
  let shown = 0;
  const groups = BATCH_GROUPS.map((g) => {
    const mine = rows.filter((r) => r.status !== 'rejected' && g.kinds.includes(r.kind));
    const ordered = treeOrder(mine).slice(0, Math.max(budget, 0));
    budget -= ordered.length;
    shown += ordered.length;
    const heading = headingOf(g.label, mine, mine.length >= groupCount(job.counts.byKind, g.kinds));
    return { ...g, all: mine, ordered, heading };
  }).filter((g) => g.ordered.length > 0);
  const listed = rows.filter((r) => r.status !== 'rejected').length;
  const more = Math.max(rowTotal(job.counts.byKind), listed) - shown;
  const rejected = rows.filter((r) => r.status === 'rejected');

  return (
    <div data-batch-rows="">
      {groups.map((g) => (
        <section key={g.label} data-batch-group={g.label} aria-label={g.label}>
          <h4 className={cn('flex items-baseline justify-between gap-2 px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.04em]', MUTED)}>
            <span className="min-w-0 truncate" title={g.heading.text}>{g.heading.text}</span>
            <span>{Math.max(groupCount(job.counts.byKind, g.kinds), g.all.length)}</span>
          </h4>
          {place !== undefined && job.anchorItemId === null && g.kinds.includes('task') && (
            <PlaceUnder job={job} rows={g.all} place={place} />
          )}
          <ul>
            {g.ordered.map(({ row, depth }) => (
              <BatchRow key={row.id} job={job} row={row} depth={depth} marked={changed?.get(row.id)}
                projectInHeading={g.heading.project} />
            ))}
          </ul>
        </section>
      ))}
      {more > 0 && (
        <p className={cn('border-t border-[hsl(var(--h-border))] px-3 py-2 text-xs', MUTED)}>
          {`và ${more} dòng nữa · `}
          <Link to={`/assistant/batches/${job.id}`} onClick={() => { if (narrow) setOpen(false); }}
            className="inline-flex min-h-11 items-center font-semibold text-[hsl(var(--h-primary))] hover:underline lg:min-h-0">Mở rộng</Link>
        </p>
      )}
      <Rejected rows={rejected} />
    </div>
  );
}
